import { ThemeProvider, CssBaseline } from '@mui/material';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import theme from './src/theme';
import HeroSection from './src/components/HeroSection';
import FeaturesSection from './src/components/FeaturesSection';
import HowItWorksSection from './src/components/HowItWorksSection';
import PricingSection from './src/components/PricingSection';
import ImageCarousel from './src/components/ImageCarousel';
import WaitlistSection from './src/components/WaitlistSection';
import FAQSection from './src/components/FAQSection';
import Footer from './src/components/Footer';
import SignIn from './src/pages/SignIn';
import SignUp from './src/pages/SignUp';
import ForgotPassword from './src/pages/ForgotPassword';
import ResetPassword from './src/pages/ResetPassword';
import CompleteProfile from './src/pages/CompleteProfile';
import Dashboard from './src/pages/Dashboard';
import Campaigns from './src/pages/Campaigns';
import Customers from './src/pages/Customers';
import CampaignDetails from './src/pages/CampaignDetails';
import CreateCampaign from './src/pages/CreateCampaign';
import CreateSurvey from './src/pages/CreateSurvey';
import ManualSurveyBuilder from './src/pages/ManualSurveyBuilder';
import AISurveyBuilder from './src/pages/AISurveyBuilder';
import CustomerUpload from './src/pages/CustomerUpload';
import RewardsManagement from './src/pages/RewardsManagement';
import Reviews from './src/pages/Reviews';
import CustomerSurvey from './src/pages/CustomerSurvey';
import ReviewReady from './src/pages/ReviewReady';
import ThankYou from './src/pages/ThankYou';
import Settings from './src/pages/Settings';
import Notifications from './src/pages/Notifications';
import HelpCenter from './src/pages/HelpCenter';
import TicketDetails from './src/pages/TicketDetails';
import NewTicket from './src/pages/NewTicket';
import ProtectedRoute from './src/components/auth/ProtectedRoute';
import { useAnalytics } from './src/hooks/useAnalytics';

const createEmotionCache = () => {
  return createCache({
    key: 'mui',
    prepend: true,
  });
};

const emotionCache = createEmotionCache();

const LandingPage = () => {
  console.log('🏠 Landing Page rendering...');
  return (
    <>
      <HeroSection />
      <FeaturesSection />
      <HowItWorksSection />
      <PricingSection />
      <ImageCarousel />
      <WaitlistSection />
      <FAQSection />
      <Footer />
    </>
  );
};

const App = () => {
  // Initialize Google Analytics (already loaded in HTML)
  useAnalytics();
  
  console.log('🚀 App component rendering...');

  const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <CacheProvider value={emotionCache}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/survey/:campaignId/:uniqueToken/:accessHash" element={<CustomerSurvey />} />
            <Route path="/review-ready" element={<ReviewReady />} />
            <Route path="/survey/thank-you" element={<ThankYou />} />
            
            {/* Protected Routes */}
            <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><Customers /></ProtectedRoute>} />
            <Route path="/campaigns" element={<ProtectedRoute><Campaigns /></ProtectedRoute>} />
            <Route path="/campaigns/create" element={<ProtectedRoute><CreateCampaign /></ProtectedRoute>} />
            <Route path="/campaigns/:campaignId" element={<ProtectedRoute><CampaignDetails /></ProtectedRoute>} />
            <Route path="/campaigns/create/survey" element={<ProtectedRoute><CreateSurvey /></ProtectedRoute>} />
            <Route path="/campaigns/create/survey/manual" element={<ProtectedRoute><ManualSurveyBuilder /></ProtectedRoute>} />
            <Route path="/campaigns/create/survey/ai" element={<ProtectedRoute><AISurveyBuilder /></ProtectedRoute>} />
            <Route path="/campaigns/create/customers" element={<ProtectedRoute><CustomerUpload /></ProtectedRoute>} />
            <Route path="/rewards" element={<ProtectedRoute><RewardsManagement /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute><Reviews /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
            <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
            <Route path="/help" element={<ProtectedRoute><HelpCenter /></ProtectedRoute>} />
            <Route path="/help/new-ticket" element={<ProtectedRoute><NewTicket /></ProtectedRoute>} />
            <Route path="/help/ticket/:ticketId" element={<ProtectedRoute><TicketDetails /></ProtectedRoute>} />
            
            {/* Fallback */}
            <Route path="*" element={<LandingPage />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </CacheProvider>
    </GoogleOAuthProvider>
  );
};

export default App;